import request from '@/utils/request'

let loadingPromise = null
let searchServicesPromise = null

const AMAP_SCRIPT_TIMEOUT_MS = 12000
const AMAP_PLUGIN_TIMEOUT_MS = 6000
const AMAP_SEARCH_TIMEOUT_MS = 8000
const SERVER_SEARCH_TIMEOUT_MS = 6000
const DEFAULT_SEARCH_CITY = '上海'

export function getAmapKey() {
  return (process.env.VUE_APP_AMAP_KEY || '').trim()
}

export function getAmapSecurityCode() {
  return (process.env.VUE_APP_AMAP_SECURITY_CODE || '').trim()
}

function withTimeout(promise, timeoutMs, message) {
  let timer = null
  return new Promise((resolve, reject) => {
    timer = setTimeout(() => reject(new Error(message)), timeoutMs)
    promise.then(resolve, reject).finally(() => clearTimeout(timer))
  })
}

function applyAmapSecurityConfig() {
  const securityJsCode = getAmapSecurityCode()
  if (!securityJsCode || typeof window === 'undefined') return
  window._AMapSecurityConfig = {
    ...(window._AMapSecurityConfig || {}),
    securityJsCode
  }
}

/** 动态加载高德 JS API 2.0 */
export function loadAmap() {
  const key = getAmapKey()
  if (!key) {
    return Promise.reject(new Error('未配置高德地图 JS API Key'))
  }
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('当前环境不支持地图搜索'))
  }
  if (window.AMap) return Promise.resolve(window.AMap)
  if (loadingPromise) return loadingPromise

  applyAmapSecurityConfig()

  const promise = new Promise((resolve, reject) => {
    const script = document.createElement('script')
    let settled = false
    const finish = (fn, value) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      script.onload = null
      script.onerror = null
      fn(value)
    }
    const timer = setTimeout(() => {
      if (script.parentNode) script.parentNode.removeChild(script)
      finish(reject, new Error('高德地图脚本加载超时'))
    }, AMAP_SCRIPT_TIMEOUT_MS)

    script.type = 'text/javascript'
    script.async = true
    script.dataset.amapLoader = 'device-picker'
    script.src = `https://webapi.amap.com/maps?v=2.0&key=${encodeURIComponent(key)}&plugin=AMap.PlaceSearch,AMap.Geocoder`
    script.onload = () => {
      if (window.AMap) {
        finish(resolve, window.AMap)
        return
      }
      setTimeout(() => {
        if (window.AMap) finish(resolve, window.AMap)
        else finish(reject, new Error('高德地图初始化失败'))
      }, 200)
    }
    script.onerror = () => finish(reject, new Error('高德地图脚本加载失败'))
    document.head.appendChild(script)
  })

  loadingPromise = promise.catch(err => {
    loadingPromise = null
    throw err
  })
  return loadingPromise
}

function loadSearchServices(AMap) {
  const promise = new Promise((resolve, reject) => {
    try {
      AMap.plugin(['AMap.PlaceSearch', 'AMap.Geocoder'], () => {
        try {
          resolve({
            placeSearchShanghai: new AMap.PlaceSearch({
              pageSize: 10,
              city: '上海',
              citylimit: false,
              extensions: 'base'
            }),
            placeSearch: new AMap.PlaceSearch({
              pageSize: 10,
              city: '全国',
              citylimit: false,
              extensions: 'base'
            }),
            geocoderShanghai: new AMap.Geocoder({ city: '上海' }),
            geocoder: new AMap.Geocoder({ city: '全国' })
          })
        } catch (e) {
          reject(e)
        }
      })
    } catch (e) {
      reject(e)
    }
  })
  return withTimeout(promise, AMAP_PLUGIN_TIMEOUT_MS, '高德地图搜索插件加载超时')
}

async function getSearchServices() {
  if (!searchServicesPromise) {
    searchServicesPromise = loadAmap()
      .then(loadSearchServices)
      .catch(err => {
        searchServicesPromise = null
        throw err
      })
  }
  return searchServicesPromise
}

function normalizeLocation(raw) {
  if (!raw) return null
  const lng = Number(raw.lng)
  const lat = Number(raw.lat)
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null
  return {
    lng,
    lat,
    name: raw.name || '',
    address: raw.address || ''
  }
}

async function serverGeocodeAsync(keyword) {
  const res = await request.get('/gas/map/geocode', {
    params: { keyword, city: DEFAULT_SEARCH_CITY },
    skipErrorHandler: true,
    timeout: SERVER_SEARCH_TIMEOUT_MS
  })
  const data = res.data || {}
  const loc = normalizeLocation(data)
  if (loc) return loc
  if (data.error) throw new Error(data.error)
  return null
}

function placeSearchAsync(placeSearch, keyword) {
  const promise = new Promise((resolve, reject) => {
    try {
      placeSearch.search(keyword, (status, result) => {
        if (status !== 'complete' || !result || !result.poiList || !result.poiList.pois.length) {
          resolve(null)
          return
        }
        const poi = result.poiList.pois[0]
        const loc = poi.location
        resolve(normalizeLocation({
          lng: loc && typeof loc.getLng === 'function' ? loc.getLng() : loc && loc.lng,
          lat: loc && typeof loc.getLat === 'function' ? loc.getLat() : loc && loc.lat,
          name: poi.name,
          address: poi.address
        }))
      })
    } catch (e) {
      reject(e)
    }
  })
  return withTimeout(promise, AMAP_SEARCH_TIMEOUT_MS, '高德 POI 搜索超时')
}

function geocodeAsync(geocoder, keyword) {
  const promise = new Promise((resolve, reject) => {
    try {
      geocoder.getLocation(keyword, (status, result) => {
        if (status !== 'complete' || !result || !result.geocodes || !result.geocodes.length) {
          resolve(null)
          return
        }
        const item = result.geocodes[0]
        const loc = item.location
        resolve(normalizeLocation({
          lng: loc && typeof loc.getLng === 'function' ? loc.getLng() : loc && loc.lng,
          lat: loc && typeof loc.getLat === 'function' ? loc.getLat() : loc && loc.lat,
          name: item.formattedAddress || item.formatted_address,
          address: item.formattedAddress || item.formatted_address
        }))
      })
    } catch (e) {
      reject(e)
    }
  })
  return withTimeout(promise, AMAP_SEARCH_TIMEOUT_MS, '高德地址解析超时')
}

/** 关键词 POI / 地理编码搜索，返回 { lng, lat } */
export async function geocodeKeyword(keyword) {
  const text = (keyword || '').trim()
  if (!text) return null

  let serverError = null
  let clientError = null
  try {
    const loc = await serverGeocodeAsync(text)
    if (loc) return loc
  } catch (e) {
    serverError = e
  }

  if (getAmapKey()) {
    try {
      const { placeSearchShanghai, placeSearch, geocoderShanghai, geocoder } = await getSearchServices()
      const attempts = [
        () => placeSearchAsync(placeSearchShanghai, text),
        () => placeSearchAsync(placeSearch, text),
        () => geocodeAsync(geocoderShanghai, text),
        () => geocodeAsync(geocoder, text)
      ]

      for (const attempt of attempts) {
        try {
          const loc = await attempt()
          if (loc) return loc
        } catch (e) {
          clientError = e
        }
      }
    } catch (e) {
      clientError = e
    }
  }

  if (clientError) throw clientError
  if (!getAmapKey() && serverError) throw serverError
  if (!getAmapKey()) throw new Error('未配置高德地图搜索 Key')
  return null
}

/** 打开选点弹窗时可预加载搜索插件 */
export function preloadAmapSearch() {
  if (!getAmapKey()) return Promise.resolve(null)
  return getSearchServices()
}

export function formatCoord(value) {
  if (value == null || value === '') return ''
  const n = Number(value)
  if (Number.isNaN(n)) return String(value)
  return n.toFixed(6)
}
